import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

function detectPlatform(): "ios" | "android" | "desktop" | "unknown" {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return "android";
  const isIos =
    /iphone|ipad|ipod/i.test(ua) ||
    (/macintosh/i.test(ua) && typeof document !== "undefined" && "ontouchend" in document);
  if (isIos) return "ios";
  return "desktop";
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

// Module-level cache so a button mounted AFTER beforeinstallprompt fired
// still picks up the deferred event.
let cachedPrompt: BeforeInstallPromptEvent | null = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    cachedPrompt = e as BeforeInstallPromptEvent;
    // eslint-disable-next-line no-console
    console.info("[PWA] beforeinstallprompt captured", {
      platforms: (e as BeforeInstallPromptEvent & { platforms?: string[] }).platforms,
    });
    window.dispatchEvent(new CustomEvent("pwa:promptready"));
  });
  window.addEventListener("appinstalled", () => {
    // eslint-disable-next-line no-console
    console.info("[PWA] appinstalled");
    cachedPrompt = null;
  });
}

export function InstallAppButton({
  className = "",
  label = "Install app",
  requirePrompt = false,
}: {
  className?: string;
  label?: string;
  /** If true, the button only renders when a real install prompt is available. */
  requirePrompt?: boolean;
}) {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(cachedPrompt);
  const [installed, setInstalled] = useState(false);
  const [showIos, setShowIos] = useState(false);
  const [platform, setPlatform] = useState<ReturnType<typeof detectPlatform>>("unknown");

  useEffect(() => {
    setPlatform(detectPlatform());
    setInstalled(isStandalone());
    setDeferred(cachedPrompt);

    const onReady = () => setDeferred(cachedPrompt);
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("pwa:promptready", onReady);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("pwa:promptready", onReady);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;
  if (requirePrompt && !deferred) return null;

  const handleClick = async () => {
    // eslint-disable-next-line no-console
    console.info("[PWA] install click", { hasPrompt: !!deferred, platform });
    if (deferred) {
      await deferred.prompt();
      const choice = await deferred.userChoice.catch(() => null);
      // eslint-disable-next-line no-console
      console.info("[PWA] userChoice", choice);
      cachedPrompt = null;
      setDeferred(null);
      return;
    }
    if (platform === "ios") {
      setShowIos(true);
      return;
    }
    window.location.href = "/install";
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={
          className ||
          "inline-flex items-center gap-2 border border-accent bg-accent px-4 py-2 text-[10px] tracking-wide-luxury text-primary-foreground transition-colors hover:bg-accent/90"
        }
        aria-label={label}
      >
        <svg
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 3v12" />
          <path d="m7 10 5 5 5-5" />
          <path d="M5 21h14" />
        </svg>
        {label}
      </button>

      {showIos && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Add to Home Screen instructions"
          className="fixed inset-0 z-[70] flex items-end justify-center bg-black/60 px-4 py-6 md:items-center"
          onClick={() => setShowIos(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md border border-border bg-background p-6 text-foreground"
          >
            <p className="text-[10px] tracking-wide-luxury text-accent">iOS · Safari</p>
            <h2 className="mt-2 font-display text-2xl">Add to Home Screen</h2>
            <ol className="mt-4 space-y-3 text-sm text-muted-foreground">
              <li>
                1. Tap the <span className="text-foreground">Share</span> button at the
                bottom of Safari.
              </li>
              <li>
                2. Scroll and tap{" "}
                <span className="text-foreground">Add to Home Screen</span>.
              </li>
              <li>
                3. Tap <span className="text-foreground">Add</span> in the top-right
                corner.
              </li>
            </ol>
            <p className="mt-4 text-xs text-muted-foreground">
              Open this site in Safari (not an in-app browser) to see the option.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <Link
                to="/install"
                className="border border-border px-4 py-2 text-[10px] tracking-wide-luxury text-muted-foreground hover:border-accent hover:text-accent"
              >
                Full instructions
              </Link>
              <button
                type="button"
                onClick={() => setShowIos(false)}
                className="bg-accent px-4 py-2 text-[10px] tracking-wide-luxury text-primary-foreground hover:bg-accent/90"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
